/**
 * SquadBannerStack — renders SquadBanner rows for the current session's
 * active squads (max N=2 visible by default) + an expandable
 * "+N more" chip for overflow, then terminal squads underneath.
 *
 * Per cycle-2 UX F9: visible banner cap prevents N=8-squad sessions from
 * pushing the message list out of view. Overflow chip expands inline
 * (no separate modal).
 *
 * Per SP3-MF-07: rendered as shrink-0 sibling between header and
 * MessageList so the parent flex chain (h-full → flex-col) keeps the
 * MessageList growable.
 */
import React, { useEffect, useState } from 'react';
import { useActiveSquads } from '../../hooks/useActiveSquads';
import { SquadBanner } from './SquadBanner';
import { FirstSquadOnboarding } from './FirstSquadOnboarding';


const VISIBLE_LIMIT = 2;
// SP4 L.1 — localStorage key for the first-squad onboarding ack.
// No `users.metadata` JSONB column exists in v0 (per SP4 §0.6); a
// migration just for one onboarding bit isn't worth the churn.
const ONBOARDING_ACK_KEY = 'sqdr-first-squad-onboarding-acked';


function _readAcked(): boolean {
  try {
    return localStorage.getItem(ONBOARDING_ACK_KEY) === 'true';
  } catch {
    return true;  // localStorage unavailable → suppress the banner
  }
}


function _writeAcked(): void {
  try {
    localStorage.setItem(ONBOARDING_ACK_KEY, 'true');
  } catch {
    // ignore quota / privacy-mode failures
  }
}


interface SquadBannerStackProps {
  sessionId: string;
  /** Optional per-squad cost cap for the CostGlyph ratio (defaults inside SquadBanner). */
  perSquadCostCap?: number;
}


export const SquadBannerStack: React.FC<SquadBannerStackProps> = ({
  sessionId, perSquadCostCap,
}) => {
  const { activeSquads, terminalSquads } = useActiveSquads(sessionId);
  const [overflowExpanded, setOverflowExpanded] = useState(false);

  // SP4 L.1 — first-squad onboarding banner. Tracks ack via
  // localStorage (no server users.metadata column in v0). The banner
  // surfaces once when the operator's first active squad lands; the
  // ack persists across sessions.
  const [acked, setAcked] = useState<boolean>(() => _readAcked());
  const [onboardingTarget, setOnboardingTarget] = useState<{
    squad: typeof activeSquads[number]['squad'];
    members: typeof activeSquads[number]['members'];
  } | null>(null);

  useEffect(() => {
    if (acked) return;
    if (activeSquads.length === 0) return;
    if (onboardingTarget) return;  // already pinned to first observed
    const first = activeSquads[0];
    setOnboardingTarget({ squad: first.squad, members: first.members });
  }, [acked, activeSquads, onboardingTarget]);

  const handleAcknowledge = () => {
    _writeAcked();
    setAcked(true);
    setOnboardingTarget(null);
  };

  // Nothing to show → don't take up any space.
  if (activeSquads.length === 0 && terminalSquads.length === 0
      && !onboardingTarget) {
    return null;
  }

  const visibleActive = activeSquads.slice(0, VISIBLE_LIMIT);
  const overflowActive = activeSquads.slice(VISIBLE_LIMIT);
  const overflowCount = overflowActive.length;

  return (
    <div
      data-squad-banner-stack
      className="flex flex-col gap-1 shrink-0 px-2 py-1"
    >
      {onboardingTarget && !acked && (
        <FirstSquadOnboarding
          squad={onboardingTarget.squad}
          members={onboardingTarget.members}
          sessionId={sessionId}
          onAcknowledge={handleAcknowledge}
        />
      )}
      {visibleActive.map((s) => (
        <SquadBanner
          key={s.squad.id}
          squad={s.squad}
          members={s.members}
          perSquadCostCap={perSquadCostCap}
        />
      ))}
      {overflowCount > 0 && !overflowExpanded && (
        <button
          onClick={() => setOverflowExpanded(true)}
          className="text-xs text-slate-500 dark:text-slate-400
                     hover:text-slate-700 dark:hover:text-slate-200
                     self-start px-2 py-0.5"
        >
          +{overflowCount} more squad{overflowCount === 1 ? '' : 's'} running ▼
        </button>
      )}
      {overflowExpanded && overflowActive.map((s) => (
        <SquadBanner
          key={s.squad.id}
          squad={s.squad}
          members={s.members}
          perSquadCostCap={perSquadCostCap}
        />
      ))}
      {terminalSquads.map((s) => (
        <SquadBanner
          key={s.squad.id}
          squad={s.squad}
          members={s.members}
          perSquadCostCap={perSquadCostCap}
        />
      ))}
    </div>
  );
};
